// ---------------------------------------------------------------------------
// Platform module — Support Sessions list page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

export function SupportSessionsPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const columns = useMemo<Column<admin.SupportSessionSummary>[]>(
    () => [
      {
        key: 'id',
        header: t('supportSessions.columns.id'),
        render: (s) => (
          <span className="text-sm font-mono text-ink">{s.id}</span>
        ),
      },
      {
        key: 'supportAccessGrantId',
        header: t('supportSessions.columns.grantId'),
        render: (s) => (
          <span className="text-sm font-mono text-ink-muted">
            {s.supportAccessGrantId}
          </span>
        ),
      },
      {
        key: 'platformMemberId',
        header: t('supportSessions.columns.operator'),
        render: (s) => (
          <span className="text-sm text-ink-muted">{s.platformMemberId}</span>
        ),
      },
      {
        key: 'status',
        header: t('supportSessions.columns.status'),
        render: (s) => <StatusBadge value={s.status} />,
      },
      {
        key: 'startedAt',
        header: t('supportSessions.columns.started'),
        render: (s) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(s.startedAt, { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        ),
      },
      {
        key: 'endedAt',
        header: t('supportSessions.columns.ended'),
        render: (s) => (
          <span className="text-sm text-ink-muted">
            {s.endedAt
              ? f.date(s.endedAt, { dateStyle: 'medium', timeStyle: 'short' })
              : '--'}
          </span>
        ),
      },
    ],
    [f, t],
  );
  const [sessions, setSessions] = useState<admin.SupportSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin
      .listSupportSessions()
      .then((res) => setSessions(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('supportSessions.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadSessions} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('supportSessions.headings.title')}</h1>
      </div>
      <DataTable
        columns={columns}
        data={sessions}
        rowKey={(s) => s.id}
        emptyMessage={t('supportSessions.empty')}
        ariaLabel={t('supportSessions.ariaLabel')}
      />
    </div>
  );
}
